import { z } from 'zod';
export * from './correlations.js';
export const ScopeSchema = z.object({ organizationId: z.string().guid() }).strict();
const Id = z.string().guid();
const Minor = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const CallStateSchema = z.enum([
    'queued',
    'dialing',
    'answered',
    'connecting_agent',
    'in_conversation',
    'ended',
]);
export const OutcomeSchema = z.enum([
    'unknown',
    'completed',
    'no_answer',
    'busy',
    'rejected',
    'failed',
    'agent_connection_failed',
    'cancelled',
    'max_duration',
]);
export const ResultsSchema = z.enum(['not_applicable', 'pending', 'ready', 'failed']);
export const CreateAttemptSchema = ScopeSchema.extend({
    projectId: Id,
    assignmentId: Id,
    participantId: Id,
    actorUserId: z.string().min(1).max(200),
    idempotencyKey: z.string().min(1).max(128),
    configVersion: z.number().int().nonnegative(),
    permissionVersion: z.number().int().positive(),
    agentReference: z.string().min(1).max(200),
    destinationE164: z.string().regex(/^\+[1-9]\d{7,14}$/),
    callerE164: z.string().regex(/^\+[1-9]\d{7,14}$/),
    reservationMinor: Minor.refine((value) => value > 0),
    currency: z.string().regex(/^[A-Z]{3}$/),
}).strict();
export const AttemptSchema = ScopeSchema.extend({
    id: Id,
    projectId: Id,
    assignmentId: Id,
    participantId: Id,
    request: CreateAttemptSchema,
    sessionId: Id.nullable(),
    externalSessionId: z.string().min(1).max(512).nullable(),
    conversationId: z.string().min(1).max(200).nullable(),
    dispatchKey: Id,
    dispatchState: z.enum(['not_requested', 'unknown', 'confirmed']),
    status: CallStateSchema,
    outcome: OutcomeSchema.nullable(),
    resultsStatus: ResultsSchema,
    version: z.number().int().positive(),
    leaseToken: Id.nullable(),
    leaseExpiresAt: z.string().datetime().nullable(),
    settledMinor: Minor.nullable(),
    retryAt: z.string().datetime().nullable(),
    recoveryAttempts: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
}).strict();
export const ClaimSchema = ScopeSchema.extend({
    attemptId: Id.optional(),
    limit: z.number().int().min(1).max(100).default(10),
}).strict();
export const ClaimsSchema = z.object({ items: z.array(AttemptSchema) }).strict();
export const WriteSchema = ScopeSchema.extend({
    expectedVersion: z.number().int().positive(),
    leaseToken: Id,
}).strict();
export const CheckpointSchema = WriteSchema.extend({
    sessionId: Id.optional(),
    dispatchState: z.enum(['unknown', 'confirmed']).optional(),
    externalSessionId: z.string().min(1).max(512).optional(),
    conversationId: z.string().min(1).max(200).optional(),
    resultsStatus: ResultsSchema.optional(),
    retryAt: z.string().datetime().optional(),
    releaseLease: z.boolean().optional(),
}).strict();
export const ObservationSchema = WriteSchema.extend({
    observationId: z.string().min(1).max(200),
    status: CallStateSchema,
    outcome: OutcomeSchema.optional(),
})
    .strict()
    .refine((value) => value.status === 'ended' || value.outcome === undefined, 'Outcome requires an ended call');
export const SettlementSchema = WriteSchema.extend({
    actualMinor: Minor,
    currency: z.string().regex(/^[A-Z]{3}$/),
}).strict();
export const AttemptEventSchema = z
    .object({
    subject: z.enum(['outbound-calls-data.attempt.created', 'outbound-calls-data.attempt.updated']),
    occurredAt: z.string().datetime(),
    resourceId: z
        .object({
        objectId: Id,
        service: z.literal('outbound-calls-data'),
        objectType: z.literal('call_attempt'),
    })
        .strict(),
    data: ScopeSchema.extend({
        callAttemptId: Id,
        projectId: Id,
        assignmentId: Id,
        version: z.number().int().positive(),
    }).strict(),
})
    .strict();
export const IdempotencyLookupSchema = ScopeSchema.extend({
    projectId: z.string().guid(),
    idempotencyKey: z.string().min(1).max(128),
}).strict();
export const AssignmentLookupSchema = ScopeSchema.extend({
    projectId: Id,
    assignmentId: Id,
}).strict();
//# sourceMappingURL=index.js.map